import React from "react";
import Button from "@mui/material/Button";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import { Box, LinearProgress, Stack } from "@mui/material";
import DatabaseSetupTasks from "src/scenes/integrations/notion-setup/database-setup-tasks";
import DatabaseSetupSprints from "src/scenes/integrations/notion-setup/database-setup-sprints";
import { format, parseISO } from "date-fns";

const MultipleDatabaseSetup = ({
    userDatabases,
    selectedDatabases,
    setSelectedDatabases,
    setHasError,
    title,
    taskDatabasesCount,
    sprintDatabasesCount,
    databasesFullPaths,
    setAllTaskFields,
    allBugTypes,
    setAllBugTypes,
    allSprintFields,
    setAllSprintFields,
    loadingPathId,
}) => {
    const handleAddDatabase = () => {
        if (userDatabases && userDatabases.length > 0) {
            const defaultDb = userDatabases.find(
                (db) => db.title && db.title[0] && db.title[0].plain_text.includes(title)
            );
            if (!defaultDb) {
                const firstAvailableDb = userDatabases.find(
                    (db) => !selectedDatabases.some((selectedDb) => selectedDb.id === db.id)
                );
                if (firstAvailableDb) {
                    setSelectedDatabases((prevSelectedDatabase) => {
                        const isAlreadySelected = prevSelectedDatabase.some(
                            (db) => db.id === firstAvailableDb.id
                        );
                        if (!isAlreadySelected) {
                            return [...prevSelectedDatabase, firstAvailableDb];
                        }
                        return prevSelectedDatabase;
                    });
                }
                return;
            }
            setSelectedDatabases((prevSelectedDatabase) => {
                const isAlreadySelected = prevSelectedDatabase.some((db) => db.id === defaultDb.id);
                if (!isAlreadySelected) {
                    return [...prevSelectedDatabase, defaultDb];
                }
                return prevSelectedDatabase;
            });
            const nextDb = userDatabases.find(
                (db) =>
                    db.id !== defaultDb.id &&
                    !selectedDatabases.some((selectedDb) => selectedDb.id === db.id)
            );

            if (nextDb) {
                setSelectedDatabases((prevSelectedDatabase) => {
                    const isNextDbAlreadySelected = prevSelectedDatabase.some(
                        (db) => db.id === nextDb.id
                    );
                    if (!isNextDbAlreadySelected) {
                        return [...prevSelectedDatabase, nextDb];
                    }
                    return prevSelectedDatabase;
                });
            }
        }
    };

    const handleRemoveLastDatabase = () => {
        if (selectedDatabases.length) {
            const lastDatabaseId = selectedDatabases[selectedDatabases.length - 1].id;
            setSelectedDatabases((prevSelectedDatabases) => {
                const updatedDatabases = prevSelectedDatabases.slice(0, -1);
                return updatedDatabases;
            });
            if (title === "Tasks") {
                setAllTaskFields((prevFields) => {
                    return prevFields.filter((field) => field.databaseId !== lastDatabaseId);
                });
                setAllBugTypes((prevBugTypes) => {
                    const updatedBugTypes = { ...prevBugTypes };
                    delete updatedBugTypes[lastDatabaseId];
                    return updatedBugTypes;
                });
            }
            if (title === "Sprints")
                setAllSprintFields((prevFields) => {
                    return prevFields.filter((field) => field.databaseId !== lastDatabaseId);
                });
        }
    };

    React.useEffect(() => {
        if (userDatabases && userDatabases.length > 0 && selectedDatabases.length === 0) {
            const defaultDb = userDatabases.find(
                (db) => db.title && db.title[0] && db.title[0].plain_text.includes(title)
            );
            if (defaultDb) {
                setSelectedDatabases([defaultDb]);
            }
        }
    }, [userDatabases]);

    return (
        <>
            {selectedDatabases &&
                selectedDatabases.map((db) => (
                    <Accordion key={db.id} style={{ marginTop: "18px", marginBottom: "16px" }}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls={`panel${db.id}-content`}
                            id={`panel${db.id}-header`}
                        >
                            <Stack direction="column" spacing={1} style={{ width: "100%" }}>
                                <p
                                    style={{
                                        marginBottom: "6px",
                                        marginTop: 0,
                                    }}
                                >
                                    {(db.title[0] && db.title[0].plain_text) || "No name"}
                                </p>
                                {loadingPathId.includes(db.id) ? (
                                    <Box sx={{ width: "50%" }}>
                                        <LinearProgress color="secondary" />
                                    </Box>
                                ) : null}
                                <p style={{ fontSize: "12px", opacity: "0.9", margin: 0 }}>
                                    {databasesFullPaths[db.id]}
                                </p>
                                {db.created_time && (
                                    <p style={{ fontSize: "12px", opacity: "0.9", margin: 0 }}>
                                        (created at: {format(parseISO(db.created_time), "d MMM yy")}
                                        )
                                    </p>
                                )}
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                            {title === "Tasks" ? (
                                <DatabaseSetupTasks
                                    userDatabases={userDatabases}
                                    selectedDatabase={db}
                                    setSelectedDatabases={(updatedDb) => {
                                        setSelectedDatabases((prevSelectedDatabase) =>
                                            prevSelectedDatabase.map((item) =>
                                                item.id === db.id ? updatedDb : item
                                            )
                                        );
                                    }}
                                    setAllTaskFields={setAllTaskFields}
                                    databasesFullPaths={databasesFullPaths}
                                    setHasError={setHasError}
                                    title={title}
                                    allBugTypes={allBugTypes}
                                    setAllBugTypes={setAllBugTypes}
                                />
                            ) : (
                                <DatabaseSetupSprints
                                    userDatabases={userDatabases}
                                    selectedDatabase={db}
                                    databasesFullPaths={databasesFullPaths}
                                    setSelectedDatabases={(updatedDb) => {
                                        setSelectedDatabases((prevSelectedDatabase) =>
                                            prevSelectedDatabase.map((item) =>
                                                item.id === db.id ? updatedDb : item
                                            )
                                        );
                                    }}
                                    setHasError={setHasError}
                                    title={title}
                                    allSprintFields={allSprintFields}
                                    setAllSprintFields={setAllSprintFields}
                                />
                            )}
                        </AccordionDetails>
                    </Accordion>
                ))}
            <Stack direction="row" style={{ width: "100%" }} justifyContent="space-between">
                <Button
                    startIcon={<AddCircleIcon />}
                    disabled={taskDatabasesCount < sprintDatabasesCount}
                    onClick={handleAddDatabase}
                >
                    Add Database
                </Button>
                <Button
                    startIcon={<RemoveCircleIcon />}
                    disabled={title === "Tasks" && taskDatabasesCount <= 1}
                    onClick={handleRemoveLastDatabase}
                >
                    Remove Database
                </Button>
            </Stack>
        </>
    );
};
export default MultipleDatabaseSetup;
